import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import {
  CurrentUser,
  EnvironmentType,
  GraphDetails,
  MicrosoftTenant,
  PrivilegedTenant,
  ZpmPrivilege,
} from '../../../shared/models/generated/ziroModels';
import { MicrosoftTenantsResource } from '../../contexts/microsoft-tenants.resource';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { combineLatest, EMPTY, Observable, of, Subscription, throwError } from 'rxjs';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ZiroSelectConfig } from '../../../forms/fields/select/ziro-select.component';
import { catchError, delayWhen, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { SystemStatusContext } from '../../../shared/contexts/system-status.context';
import { MsTeamsCachedOptionsResource } from '../../resources/ms-teams-cached-options.resource';
import { ActivatedRoute, Router } from '@angular/router';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import { MicrosoftTenantsStateContext } from '../../../shared/contexts/microsoft-tenants-state.context';
import { AuthenticationContext } from '../../../shared/contexts/authentication.context';

const NAME_MAX_LENGTH = 255;

interface ZpmRoleAndPermissionRow {
  feature: string;
  required: string;
  leastPermissiveRole: string;
  higherPrivileges: string;
  enabled: string;
  cssClass: string;
}

interface ZpmCurrentRoleRow {
  graphRole: string;
}

@Component({
  selector: 'ziro-tenant-management-form',
  templateUrl: './tenant-management-form.component.html',
  styleUrls: ['../../admin-page.scss', './tenant-management-form.component.scss'],
  standalone: false,
})
export class TenantManagementFormComponent
  extends ZiroFormAbstractDirective<MicrosoftTenant>
  implements OnInit, OnDestroy
{
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _bottomNavService = inject(BottomNavService);
  private _microsoftTenantsResource = inject(MicrosoftTenantsResource);
  private _microsoftTenantsStateContext = inject(MicrosoftTenantsStateContext);
  private _toastService = inject(ToastService);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _systemStatusContext = inject(SystemStatusContext);
  private _msTeamsCachedOptionsResource = inject(MsTeamsCachedOptionsResource);
  private _authenticationContext = inject(AuthenticationContext);

  isLoading = true;
  ziroIcons = ZiroIcons;
  isRefreshing: boolean;
  allTenants: PrivilegedTenant[] = [];
  tenantId: number;
  roleAndPermissionTableData: ZpmRoleAndPermissionRow[] = [];
  currentRolesTableData: ZpmCurrentRoleRow[] = [];
  isTokenDetailsError = false;

  formConfig: ZiroFormConfig = {
    fields: {
      tenantName: {
        label: 'tkey;admin.tenant_management.config.label.tenant',
        dataAutomation: 'tenant-management-tenant-name',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        required: true,
        validation: [
          {
            validator: (val: string) =>
              val.length <= NAME_MAX_LENGTH ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
            message: {
              content: 'tkey;validators.global.error.maxlength',
              params: {
                maxlength: NAME_MAX_LENGTH,
              },
            },
          },
          {
            validator: (val) => {
              return this.allTenants
                .filter((tenant) => this.tenantId !== tenant.tenantId)
                .some((tenant) => tenant.tenantName === val)
                ? ZiroFormsValidationState.INVALID
                : ZiroFormsValidationState.VALID;
            },
            message: 'tkey;admin.tenant_management.config.tenant.unique',
          },
        ],
      },
      tenantGuid: {
        label: 'tkey;admin.tenant_management.config.label.tenant.id',
        dataAutomation: 'tenant-management-tenant-guid',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        required: true,
      },
      applicationGuid: {
        label: 'tkey;admin.tenant_management.config.label.client.id',
        dataAutomation: 'tenant-management-application-guid',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        required: true,
      },
      applicationSecret: {
        label: 'tkey;admin.tenant_management.config.label.client.secret',
        dataAutomation: 'tenant-management-application-secret',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.PASSWORD }),
        required: true,
      },
      environmentType: {
        label: 'tkey;admin.tenant_management.config.label.environment.type',
        dataAutomation: 'tenant-management-environment-type',
        componentConfig: new ZiroSelectConfig({ options: Object.values(EnvironmentType) }),
        required: true,
      },
    },
  };

  private _currentUser: CurrentUser;
  private _subs = new Subscription();

  ngOnInit(): void {
    this.tenantId = Number(this._route.snapshot.params.tenantId);
    const loadingObs: Observable<void>[] = [];

    const tenantsObs = this._microsoftTenantsStateContext.fieldConfigState$.pipe(
      map((tenants) => {
        this.allTenants = tenants.privilegedTenants;
      }),
    );
    loadingObs.push(tenantsObs);

    const authObs = this._authenticationContext.state$.pipe(
      map((currentUser) => {
        this._currentUser = currentUser;
        if (
          !this.tenantId &&
          !this._authenticationContext.userHasPrivilege(this._currentUser, ZpmPrivilege.GLOBAL_SETTINGS)
        ) {
          this._router.navigateByUrl('/');
        }
      }),
    );
    loadingObs.push(authObs);

    if (this.tenantId) {
      const getObs = this._microsoftTenantsResource.get(this.tenantId).pipe(
        switchMap((tenant) => {
          this.entitySource.next(tenant);
          this.setIsExisting(true);
          return this._getTokenDetails(this.tenantId);
        }),
      );
      loadingObs.push(getObs);
    } else {
      this.entitySource.next({
        id: null,
        tenantName: '',
        applicationGuid: '',
        applicationSecret: '',
        environmentType: EnvironmentType.COMMERCIAL,
        tenantGuid: '',
      });
    }

    combineLatest(loadingObs)
      .pipe(take(1))
      .subscribe(() => {
        this._initBottomNav();
        this.isLoading = false;
      });

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() =>
      this._bottomNavService.setBottomNavValidationError(!this.isFormValid()),
    );
    this._subs.add(formSubmittedSub);

    this.ziroFormsUpdate$.subscribe((update) => {
      this._initBreadcrumbs(update.new.tenantName);
    });
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  refreshCache(): void {
    this.isRefreshing = true;
    this._setButtonDisabledState('delete', true);
    this._setButtonDisabledState('save', true);
    this._setButtonDisabledState('refresh-token', true);

    this._msTeamsCachedOptionsResource
      .refreshTeamsCachedOptions(this.tenantId)
      .pipe(
        finalize(() => {
          this.isRefreshing = false;
          this._setButtonDisabledState('delete', false);
          this._setButtonDisabledState('save', false);
          this._setButtonDisabledState('refresh-token', false);
        }),
      )
      .subscribe(() => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          this.ziroIcons.REFRESH,
          'tkey;admin.powershell_management.toast.refresh_cache.title',
          'tkey;admin.powershell_management.toast.refresh_cache.message',
        );
      });
  }

  protected submit(): Observable<void> {
    return this._onSaveClicked();
  }

  private _onSaveClicked(): Observable<void> {
    this._setButtonPendingState('save', true);
    this._setButtonDisabledState('delete', true);
    this._setButtonDisabledState('refresh-token', true);

    // Hit the validate endpoint first. It does some extra validation on the backend such as making sure the app reg
    // in Azure is valid.
    return this._microsoftTenantsResource.validate(this.entity).pipe(
      delayWhen((tenantDetails) => {
        const hasInValidUseCases = tenantDetails.useCases.some((useCase) => useCase.isRequired && !useCase.isEnabled);
        if (hasInValidUseCases) {
          return of(null);
        }

        if (this.isExisting) {
          return this._microsoftTenantsResource.put(this.entity);
        }

        return this._microsoftTenantsResource.post(this.entity).pipe(
          map((ref) => {
            this._ziroFormStateService.setIsFormDirty(false);
            this._router.navigate(['../', ref.id, 'app-registration'], { relativeTo: this._route });
          }),
        );
      }),
      delayWhen(() => this._systemStatusContext.refreshSystemStatus()),
      map((tenantDetails) => {
        this.isTokenDetailsError = tenantDetails.useCases.length === 0;
        this._setRolesAndPermissionsTableRows(tenantDetails);
        this._setCurrentRolesTableRows(tenantDetails);

        const missingRequiredUseCases = tenantDetails.useCases.filter(
          (useCase) => useCase.isRequired && !useCase.isEnabled,
        );

        if (!!missingRequiredUseCases && missingRequiredUseCases.length > 0) {
          this._toastService.push(
            ToastTypes.ERROR,
            ZiroIcons.MICROSOFT_365_SMALL,
            'tkey;admin.tenant_management.config.toast.warning.missing_roles.title',
            this._translateService.instant('tkey;admin.tenant_management.config.toast.warning.missing_roles.message') +
              `<ul class="mb-0">${missingRequiredUseCases.map((useCase) => `<li>${useCase.leastPermission}</li>`).join('')}</ul>`,
          );
        } else {
          this._toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.MICROSOFT_365_SMALL,
            'tkey;shared.toast.save.success.title',
            'tkey;admin.tenant_management.config.toast.success.message',
          );
        }

        this._setButtonPendingState('save', false);
        this._setButtonDisabledState('delete', false);
        this._setButtonDisabledState('refresh-token', false);
      }),
      catchError((err) => {
        if (!err || err.status !== 422) {
          throw err;
        }
        const errorDescription = err ? err.error.description || err.error[0].message : '';
        this._toastService.push(
          ToastTypes.ERROR,
          ZiroIcons.MICROSOFT_365_SMALL,
          'tkey;admin.tenant_management.config.toast.error.validate.message',
          errorDescription,
        );
        this._setButtonPendingState('save', false);
        this._setButtonDisabledState('delete', false);
        this._setButtonDisabledState('refresh-token', false);
        return throwError(() => EMPTY);
      }),
    );
  }

  private _setRolesAndPermissionsTableRows(tenantDetails: GraphDetails | Partial<GraphDetails>): void {
    if (!tenantDetails) {
      this.roleAndPermissionTableData = [];
      return;
    }

    this.roleAndPermissionTableData = tenantDetails.useCases
      .sort((a, b) => {
        if (a.isRequired !== b.isRequired) {
          return Number(b.isRequired) - Number(a.isRequired);
        }

        return Number(b.isEnabled) - Number(a.isEnabled);
      })
      .map((useCase) => {
        return {
          feature: useCase.description ? useCase.description : useCase.name,
          leastPermissiveRole: `<code>${useCase.leastPermission}</code>`,
          higherPrivileges: useCase.higherPrivileges
            .map((privilege: string) => `<p class="mb-0"><code>${privilege}</code></p>`)
            .join(''),
          required: useCase.isRequired
            ? `<i class="ms-4 text-success ${this.ziroIcons.SAVE_SUCCESS}"></i>`
            : `<em>Optional</em>`,
          enabled: useCase.isEnabled
            ? `<i class="ms-4 text-success ${this.ziroIcons.SAVE_SUCCESS}"></i>`
            : `<i class="ms-4 text-danger ${this.ziroIcons.REMOVE}"></i>`,
          cssClass: useCase.isRequired && !useCase.isEnabled ? 'table-danger' : '',
        };
      });
  }

  private _setCurrentRolesTableRows(tenantDetails: GraphDetails | Partial<GraphDetails>): void {
    if (!tenantDetails) {
      this.currentRolesTableData = [];
      return;
    }
    this.currentRolesTableData = tenantDetails.graphRoles.map((graphRole) => ({
      graphRole: `<code>${graphRole}</code>`,
    }));
  }

  private _initBreadcrumbs(tenantName: string): void {
    this._breadcrumbsService.updateBreadcrumbs([
      {
        label: 'tkey;admin.tenants.title',
        routerLink: false,
      },
      {
        label: tenantName ? tenantName : 'tkey;admin.tenant_management.add.config.title',
      },
    ]);
  }

  private _initBottomNav(): void {
    const buttons: BottomNavButton[] = [];

    if (this.isExisting) {
      buttons.push({
        id: 'tenant-management-refresh-token-button',
        label: 'tkey;admin.tenant_management.config.token_refresh',
        buttonClass: ButtonStyles.DEFAULT,
        dataAutomation: 'tenant-management-refresh-token-button',
        icon: ZiroIcons.REFRESH,
        cb: () => {
          this._onRefreshTokenClicked();
        },
      });

      buttons.push({
        id: 'tenant-management-delete-button',
        label: 'tkey;dialogs.button.delete',
        buttonClass: ButtonStyles.DANGER,
        dataAutomation: 'tenant-management-delete-button',
        icon: ZiroIcons.DELETE_SMALL,
        cb: () => this._onDeleteClick(),
      });
    }

    buttons.push({
      id: 'tenant-management-save-button',
      label: 'tkey;admin.ui.save',
      buttonClass: ButtonStyles.PRIMARY,
      dataAutomation: 'tenant-management-save-button',
      icon: ZiroIcons.SAVE_SUCCESS,
      type: ButtonTypes.SUBMIT,
      submitSubject: this._validateAndSubmitSource,
    });

    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(buttons));
  }

  private _onDeleteClick(): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        modalBodyIconHeaderClass: 'animated bounceIn lead text-center text-danger',
        promptBody: this._translateService.instant('tkey;admin.tenant_management.config.modal.delete.message', {
          tenantName: this.entity.tenantName,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._deleteAppRegistrationDetails(),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _onRefreshTokenClicked(): void {
    this._setButtonPendingState('refresh-token', true);
    this._setButtonDisabledState('save', true);
    this._setButtonDisabledState('delete', true);

    this._microsoftTenantsResource
      .tokenRefresh(this.tenantId)
      .pipe(
        switchMap(() => this._getTokenDetails(this.tenantId)),
        finalize(() => {
          this._setButtonPendingState('refresh-token', false);
          this._setButtonDisabledState('save', false);
          this._setButtonDisabledState('delete', false);
        }),
      )
      .subscribe(() => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.MICROSOFT_365_SMALL,
          'tkey;admin.tenant_management.config.token_refresh.toast.title',
          'tkey;admin.tenant_management.config.token_refresh.toast.message',
        );
      });
  }

  private _getTokenDetails(tenantId: number): Observable<void> {
    return this._microsoftTenantsResource.grantedRolesAndPermissions(tenantId).pipe(
      tap((tenantDetails) => {
        // An error can manifest as either a 500, or an object with blank properties.
        this.isTokenDetailsError = tenantDetails.useCases.length === 0;
      }),
      catchError(() => {
        this.isTokenDetailsError = true;
        return of(null);
      }),
      map((tenantDetails) => {
        this._setRolesAndPermissionsTableRows(tenantDetails);
        this._setCurrentRolesTableRows(tenantDetails);
      }),
    );
  }

  private _setButtonDisabledState(button: 'save' | 'delete' | 'refresh-token', state: boolean): void {
    this._bottomNavService.setButtonDisabledState(`tenant-management-${button}-button`, state);
  }

  private _setButtonPendingState(button: 'save' | 'delete' | 'refresh-token', setting: boolean): void {
    this._bottomNavService.setButtonPendingState(`tenant-management-${button}-button`, setting);
  }

  private _deleteAppRegistrationDetails(): Observable<void> {
    this._setRolesAndPermissionsTableRows(null);
    this._setCurrentRolesTableRows(null);

    this.isLoading = true;

    return this._microsoftTenantsResource.delete(this.tenantId).pipe(
      tap(() => {
        this._toastService.push(
          ToastTypes.INFO,
          `${this.ziroIcons.DELETE_SMALL} text-danger`,
          'tkey;shared.toast.delete.success.title',
          `tkey;admin.tenant_management.config.toast.delete.message`,
        );
        this._navigateAfterDelete();
      }),
    );
  }

  private _navigateAfterDelete(): void {
    if (this._authenticationContext.userHasPrivilege(this._currentUser, ZpmPrivilege.GLOBAL_SETTINGS)) {
      this._router.navigate(['../..', 'new'], { relativeTo: this._route });
      return;
    }
    const remainingTenants = this.allTenants.filter((tenant) => tenant.tenantId !== this.tenantId);
    if (!remainingTenants.length) {
      this._authenticationContext.logout().subscribe();
      return;
    }
    const nextTenant = remainingTenants.find((tenant) => tenant.tenantId > this.tenantId);
    let goToTenantId: number;
    if (nextTenant) {
      goToTenantId = nextTenant.tenantId;
    } else {
      const lastTenant = remainingTenants[remainingTenants.length - 1];
      goToTenantId = lastTenant.tenantId;
    }
    this._router.navigate(['../..', goToTenantId, 'app-registration'], { relativeTo: this._route });
  }
}
